import type { Category } from "../types";
import {
  nativeIframeSrc,
  nativeImageSrc,
  nativeScriptSrc,
  nativeSetAttribute,
} from "../core/native";
import type { BlockedItem } from "./blocked-items";
import { markHandled } from "./blocked-items";
import { log, type BlockingContext } from "./context";

const SKIPPED_ATTRS = new Set(["type", "src", "data-ck-category", "data-ck-src", "data-ck-type"]);

/**
 * Releases everything held back for the newly granted categories, in original document
 * order. Scripts are recreated rather than revived: a `<script>` that has already been
 * inserted with `type="text/plain"` will never execute again, however you mutate it.
 */
export function releaseCategories(context: BlockingContext, categories: readonly Category[]): void {
  const pending = context.items.pendingFor(categories);
  for (const item of pending) {
    item.released = true;
    try {
      release(item);
    } catch (error) {
      console.error("[consentkit] failed to release blocked item", item.url, error);
    }
  }
  if (pending.length > 0) log(context, `released ${pending.length} item(s)`, categories);
}

/**
 * Releases exactly one held item — the "load this embed once" path, which must not
 * change the stored decision or free anything else in the same category.
 */
export function releaseItem(item: BlockedItem): void {
  if (item.released) return;
  item.released = true;
  try {
    release(item);
  } catch (error) {
    console.error("[consentkit] failed to release blocked item", item.url, error);
  }
}

function release(item: BlockedItem): void {
  if (item.kind === "script") {
    releaseScript(item);
    return;
  }

  const descriptor = item.kind === "iframe" ? nativeIframeSrc : nativeImageSrc;
  if (descriptor?.set && item.url) {
    markHandled(item.element);
    descriptor.set.call(item.element, item.url);
  }
}

function releaseScript(item: BlockedItem): void {
  const original = item.element as HTMLScriptElement;
  const replacement = document.createElement("script");
  markHandled(replacement);

  for (const attr of Array.from(original.attributes)) {
    if (SKIPPED_ATTRS.has(attr.name)) continue;
    setAttr(replacement, attr.name, attr.value);
  }

  const type = item.scriptType;
  if (type && type !== "text/plain") setAttr(replacement, "type", type);

  if (item.inline) {
    replacement.textContent = item.inline;
  } else if (item.url) {
    // Dynamically created scripts default to async; forcing async=false makes the
    // browser execute them in insertion order, preserving the page's original sequence.
    replacement.async = original.async;
    if (!original.hasAttribute("async")) replacement.async = false;
    nativeScriptSrc?.set?.call(replacement, item.url);
  }

  const parent = original.parentNode;
  if (parent) parent.replaceChild(replacement, original);
  else (document.head || document.documentElement).appendChild(replacement);
}

function setAttr(element: Element, name: string, value: string): void {
  // Our patched setAttribute would re-evaluate the blocklist; go straight to the native one.
  if (nativeSetAttribute) nativeSetAttribute.call(element, name, value);
  else element.setAttribute(name, value);
}
